import { useEffect, useState } from 'react';
import useWebSocket from 'react-use-websocket';
import { QualityCheck, Side } from 'shared-types';

import useSessionStore from 'hooks/stores/useSessionStore';

import useProductsStore from './stores/useProductsStore';

const WEB_SOCKET_URL = process.env.REACT_APP_WEB_SOCKET_URL || 'ws://localhost:8080';

// Custom reusable hook
const useRealTimeUpdateSidePreview = () => {
  const sessionId = useSessionStore((state) => state.sessionId);
  const updateSidePreview = useProductsStore((state) => state.updateSidePreview);
  const [error, setError] = useState('');

  // Alleen een geldige WebSocket URL maken als sessionId bestaat
  const socketUrl = sessionId ? `${WEB_SOCKET_URL}/${sessionId}/assets/previewImage` : null;

  const { readyState, lastMessage } = useWebSocket(socketUrl, {
    shouldReconnect: () => true,
    reconnectInterval: 3000,
    // Alleen verbinden als we een geldig socketUrl hebben
    reconnectAttempts: socketUrl ? 10 : 0,
  });

  const isVisible = readyState === 1 || readyState === 2;

  useEffect(() => {
    if (lastMessage) {
      try {
        const { data } = lastMessage;
        const parsedData = JSON.parse(data);

        const { sideId, previewImageUrl, previewThumbnailUrl, resizeMetadata, productId, qualityCheck, error } =
          parsedData as {
            sideId: string;
            productId: string;
            previewImageUrl: string;
            previewThumbnailUrl: string;
            resizeMetadata: Side['resizeMetadata'];
            qualityCheck?: QualityCheck;
            error?: string;
          };

        if (error) {
          console.error('Side preview error:', error);
          setError(error);
          return;
        }

        const cacheBuster = Date.now();
        updateSidePreview({
          sideId,
          resizeMetadata,
          previewImageUrl: `${previewImageUrl}?t=${cacheBuster}`,
          previewThumbnailUrl: `${previewThumbnailUrl}?t=${cacheBuster}`,
          productId,
          qualityCheck,
        });
      } catch (err) {
        console.error('Error parsing side preview websocket message:', err);
        setError('Error parsing websocket message');
      }
    }
  }, [lastMessage, updateSidePreview]);

  return {
    error,
    isVisible,
    connectionState: readyState,
    isConnected: readyState === 1,
  };
};

// Helper functie voor debugging in de console
if (typeof window !== 'undefined') {
  (window as any).debugHooks = {
    ...((window as any).debugHooks || {}),
    realTimeUpdateSidePreview: useRealTimeUpdateSidePreview,
  };
}

export default useRealTimeUpdateSidePreview;
